import React, { useState, useEffect } from "react";
import { FormGroup, Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faUser } from "@fortawesome/free-solid-svg-icons";
import { LabelWithToolTip } from "../../../atoms/LabelWithToolTip";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import Select from "react-select";
import LockFieldsButton from "../../../molecules/LockFieldsButton";
import { apiSaveEmployeeInformation } from "../../../../utils/queries";
import {
  validateName as validateNameRule,
  validateEmail as validateEmailRule,
  validateZip as validateZipRule,
  validateAddressLine as validateAddressLineRule,
  validateCity as validateCityRule,
  validateRequired as validateRequiredRule,
  validatePhone as validatePhoneRule,
  validateSaveForm,
} from "../../../../utils/employeeValidation";

// Adapt the shared validators to react-hook-form's `validate` contract:
// they must return either `true` or a string message. RHF's `required: true`
// already handles the empty case for every field, so each adapter just
// runs the format / length checks for non-empty values.
const validateName = (label) => {
  const fn = validateNameRule(label);
  return (value) => {
    if (!value) return true;
    return fn(value) || true;
  };
};
const validateEmail = (value) => {
  if (!value) return true;
  return validateEmailRule(value) || true;
};
const validateZip = (value) => {
  if (!value) return true;
  return validateZipRule(value) || true;
};
const validateAddressLine = (value) => {
  if (!value) return true;
  return validateAddressLineRule(value) || true;
};
const validateCity = (value) => {
  if (!value) return true;
  return validateCityRule(value) || true;
};



/* ── Reusable Section Card ── */
const SectionCard = ({ icon, title, subtitle, children }) => (
  <div className="emp-section-card">
    <div className="emp-section-card__header">
      <div className="emp-section-card__title">
        <FontAwesomeIcon icon={icon} className="emp-section-card__icon" />
        <div>
          <span>{title}</span>
          {subtitle && <small className="emp-section-card__subtitle">{subtitle}</small>}
        </div>
      </div>
    </div>
    <div className="emp-section-card__body">{children}</div>
  </div>
);



const EmployeeInformation = ({ register, alerts, setAlerts, countryList, selectedCountry, setSelectedCountry, setValue, getValues, token, employee, isExistingEmployee, errors, trigger }) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCallingCode, setCountryCallingCode] = useState("");
  const [phoneInputKey, setPhoneInputKey] = useState("init");
  const [allowEdit, setAllowEdit] = useState(false);
  const fieldsLocked = isExistingEmployee && !allowEdit;

  // Seed PhoneInput's local state from loaded employee data.
  // When a phone number is loaded, we flip phoneInputKey to force the PhoneInput
  // to remount so that its onMount fires with the auto-detected country data.
  // This is necessary because react-phone-input-2 does NOT call onChange when the
  // value prop changes externally, so we cannot rely on onChange to populate
  // countryCallingCode from a pre-filled number.
  useEffect(() => {
    if (employee) {
      if (employee.privatePhone) {
        const phone = employee.privatePhone.toString();
        setPhoneNumber(phone);
        setValue("privatePhone", phone);
      }
      if (employee.countryCallingCode) {
        const ccc = employee.countryCallingCode.toString();
        setCountryCallingCode(ccc);
        setValue("countryCallingCode", ccc);
      }
      // Force remount of PhoneInput so onMount fires with the populated phone value,
      // which lets us capture the auto-detected dialCode even when countryCallingCode
      // was not stored (e.g. 0 / null) in the database.
      setPhoneInputKey("loaded");
    }
  }, [employee]);


  const SaveEmployeeInformation = async () => {
    const formData = getValues();

    // Single source of truth: same validator the server runs (mirrored).
    const messages = validateSaveForm({
      firstName: formData.firstName,
      lastName: formData.lastName,
      privateEmail: formData.privateEmail,
      privatePhone: formData.privatePhone,
      countryCallingCode: formData.countryCallingCode,
      addressLine1: formData.addressLine1,
      city: formData.city,
      zip: formData.zip,
      country: formData.country,
      isoCountryCode: formData.isoCountryCode,
    });

    // Still call trigger so the inputs get the `is-invalid` red border via
    // formState.errors and screen readers see aria-invalid update.
    if (typeof trigger === "function") {
      await trigger([
        "firstName",
        "lastName",
        "privateEmail",
        "privatePhone",
        "addressLine1",
        "city",
        "zip",
        "country",
      ]);
    }

    if (messages.length > 0) {
      const newAlerts = messages.map((message, index) => ({
        message,
        id: `${Date.now()}-${index}`,
        color: "danger",
      }));
      setAlerts([...alerts, ...newAlerts]);
      return;
    }

    try {
      const payload = {
        firstName: formData.firstName || "",
        lastName: formData.lastName || "",
        addressLine1: formData.addressLine1 || "",
        addressLine2: formData.addressLine2 || "",
        city: formData.city || "",
        zip: formData.zip || "",
        country: formData.country ? formData.country.toString() : "",
        isoCountryCode: formData.isoCountryCode || "",
        privateEmail: formData.privateEmail || "",
        privatePhone: formData.privatePhone || "",
        countryCallingCode: parseInt(formData.countryCallingCode, 10) || 0
      };

      await apiSaveEmployeeInformation(payload, token);
      setAlerts([...alerts, { message: "Employee information saved successfully.", id: Date.now(), color: "success" }]);
    } catch (error) {
      // Surface server-side validation errors (HTTP 400 with { errors: [{ field, message }] })
      const responseErrors = error?.response?.data?.errors;
      if (Array.isArray(responseErrors) && responseErrors.length > 0) {
        const newAlerts = responseErrors.map((e, index) => ({
          message: e.message || "Invalid value.",
          id: `${Date.now()}-srv-${index}`,
          color: "danger",
        }));
        setAlerts([...alerts, ...newAlerts]);
        return;
      }
      setAlerts([...alerts, { message: "Could not save employee information. Please contact Hafnia.", id: Date.now(), color: "danger" }]);
    }
  };

  const countryOptions = countryList.map(country => ({
    label: country.countryName,
    value: country.id,
    code: country.countryCodeAlpha2
  }));



  return (
    <SectionCard
      icon={faUser}
      title="Employee Information"
      subtitle={
        isExistingEmployee
          ? "Your available details are pre-filled and locked. Click the lock icon to edit. Fields marked with * are required."
          : "Fill in your personal details below. Fields marked with * are required."
      }
    >
      {isExistingEmployee && (
        <LockFieldsButton allowEdit={allowEdit} setAllowEdit={setAllowEdit} />
      )}

      {/* Personal */}
      <div className="ei-group">
        <div className="ei-group__eyebrow">Personal</div>
        <div className="ei-grid ei-grid--2">
          <FormGroup className="ei-field">
            <LabelWithToolTip
              id="lblEmployeeFirstName"
              labelText="First name *"
              tooltipText="Employee's first name as it appears on official documents (max 100 characters)."
            />
            <input
              maxLength="100"
              {...register("firstName", { required: true, validate: validateName("First name") })}
              type="text"
              className={`form-control ei-input${errors?.firstName ? " is-invalid" : ""}`}
              disabled={fieldsLocked}
              aria-invalid={errors?.firstName ? "true" : "false"}
            />
          </FormGroup>

          <FormGroup className="ei-field">
            <LabelWithToolTip
              id="lblEmployeeLastName"
              labelText="Last name *"
              tooltipText="Employee's last name as it appears on official documents (max 100 characters)."
            />
            <input
              maxLength="100"
              {...register("lastName", { required: true, validate: validateName("Last name") })}
              type="text"
              className={`form-control ei-input${errors?.lastName ? " is-invalid" : ""}`}
              disabled={fieldsLocked}
              aria-invalid={errors?.lastName ? "true" : "false"}
            />
          </FormGroup>
        </div>
      </div>

      {/* Address */}
      <div className="ei-group">
        <div className="ei-group__eyebrow">Address</div>
        <div className="ei-grid ei-grid--2">
          <FormGroup className="ei-field">
            <LabelWithToolTip
              id="lblAddressLine1"
              labelText="Address Line 1 *"
              tooltipText="First line of the employee's private home address (max 35 characters)."
            />
            <input
              maxLength="35"
              {...register("addressLine1", { required: true, validate: validateAddressLine })}
              type="text"
              className={`form-control ei-input${errors?.addressLine1 ? " is-invalid" : ""}`}
              disabled={fieldsLocked}
              aria-invalid={errors?.addressLine1 ? "true" : "false"}
            />
          </FormGroup>

          <FormGroup className="ei-field">
            <LabelWithToolTip
              id="lblAddressLine2"
              labelText="Address Line 2"
              tooltipText="Second line of the employee's private home address, e.g. apartment or floor number (max 50 characters)."
            />
            <input
              maxLength="50"
              {...register("addressLine2", { required: false })}
              type="text"
              className="form-control ei-input"
              disabled={fieldsLocked}
            />
          </FormGroup>

          <FormGroup className="ei-field">
            <LabelWithToolTip
              id="lblCity"
              labelText="City *"
              tooltipText="City of the employee's private home address."
            />
            <input
              {...register("city", { required: true, validate: validateCity })}
              type="text"
              maxLength="30"
              className={`form-control ei-input${errors?.city ? " is-invalid" : ""}`}
              disabled={fieldsLocked}
              aria-invalid={errors?.city ? "true" : "false"}
            />
          </FormGroup>

          <FormGroup className="ei-field">
            <LabelWithToolTip
              id="lblZip"
              labelText="Zip/Postal *"
              tooltipText="Postal or ZIP code of the employee's private home address."
            />
            <input
              {...register("zip", { required: true, validate: validateZip })}
              type="text"
              className={`form-control ei-input${errors?.zip ? " is-invalid" : ""}`}
              disabled={fieldsLocked}
              aria-invalid={errors?.zip ? "true" : "false"}
            />
          </FormGroup>

          <FormGroup className="ei-field ei-field--full">
            <LabelWithToolTip
              id="lblCountry"
              labelText="Country *"
              tooltipText="Country of the employee's private home address."
            />
            <Select
              value={selectedCountry ? countryOptions.find(opt => opt.value === selectedCountry) : null}
              onChange={(e) => {
                setSelectedCountry(e?.value);
                setValue("country", e?.value || "");
                setValue("isoCountryCode", e?.code || "");
              }}
              options={countryOptions}
              placeholder="Select..."
              isDisabled={fieldsLocked}
              className={errors?.country ? "is-invalid" : ""}
              classNamePrefix="ei-select"
            />
            <input type="hidden" {...register("country", { required: true })} />
            <input type="hidden" {...register("isoCountryCode", { required: true })} />
          </FormGroup>
        </div>
      </div>

      {/* Contact */}
      <div className="ei-group">
        <div className="ei-group__eyebrow">Contact</div>
        <div className="ei-grid ei-grid--2">
          <FormGroup className="ei-field">
            <LabelWithToolTip
              id="lblEmployeeEmail"
              labelText="Private E-mail *"
              tooltipText="Employee's private e-mail address (e.g. name@example.com)."
            />
            <input
              maxLength="100"
              {...register("privateEmail", { required: true, validate: validateEmail })}
              type="text"
              className={`form-control ei-input${errors?.privateEmail ? " is-invalid" : ""}`}
              disabled={fieldsLocked}
              aria-invalid={errors?.privateEmail ? "true" : "false"}
            />
          </FormGroup>

          <FormGroup className="ei-field">
            <LabelWithToolTip
              id="lblEmployeePrivatePhone"
              labelText="Private Phone *"
              tooltipText="Employee's private phone number including country code. Must be at least 7 digits."
            />
            <PhoneInput
              key={phoneInputKey}
              value={phoneNumber}
              onMount={(value, countryData) => {
                // onMount fires when the component first mounts. We use this together
                // with phoneInputKey to capture the auto-detected dialCode after the
                // phone number has been pre-filled from the API – necessary because
                // onChange does NOT fire on external value-prop changes.
                if (countryData?.dialCode && !getValues("countryCallingCode")) {
                  setCountryCallingCode(countryData.dialCode);
                  setValue("countryCallingCode", countryData.dialCode);
                }
              }}
              onChange={(value, country) => {
                setPhoneNumber(value);
                setCountryCallingCode(country.dialCode);
                setValue("privatePhone", value);
                setValue("countryCallingCode", country.dialCode);
              }}
              inputProps={{
                // Prevent the library from auto-switching the country flag when the
                // user pastes arbitrary text. We strip the pasted content to digits,
                // then prepend the currently selected country code so the library sees
                // a valid number that starts with the correct prefix
                onPaste: (e) => {
                  e.preventDefault();
                  const pasted = (e.clipboardData || window.clipboardData).getData("text");
                  const digits = pasted.replace(/\D/g, "");
                  const currentCode = countryCallingCode || "";
                  const newValue = currentCode + digits;
                  setPhoneNumber(newValue);
                  setValue("privatePhone", newValue);
                },
              }}
              inputClass={`form-control ei-input${errors?.privatePhone ? " is-invalid" : ""}`}
              inputStyle={{ width: "100%" }}
              disabled={fieldsLocked}
            />
            <input type="hidden" {...register("privatePhone", { required: true })} />
            <input type="hidden" {...register("countryCallingCode", { required: true })} />
          </FormGroup>
        </div>
      </div>

      {/* Save */}
      <div className="ei-actions">
        <Button
          className="btn-hafnia ei-save-btn"
          type="button"
          onClick={() => SaveEmployeeInformation()}
          disabled={fieldsLocked}
        >
          Save
        </Button>
      </div>
    </SectionCard>
  );
};

export default EmployeeInformation;
